"use client";

import { useEffect, useRef, useState } from "react";
import { T } from "@/lib/i18n/T";

/**
 * Branded calendar date picker (no native <input type=date> — we want a
 * controllable, on-brand calendar). Value is an ISO `yyyy-mm-dd` string or null
 * so it maps 1:1 onto a backend field later. Month + year are selectable from
 * the header (essential for a date of birth), and future dates past `max` are
 * disabled.
 */

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const pad = (n: number) => String(n).padStart(2, "0");
const toISO = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;

function formatDisplay(iso: string | null): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return "";
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

export function DatePicker({
  value,
  onChange,
  placeholder = "Select a date",
  minYear = 1900,
}: {
  value: string | null;
  onChange: (iso: string) => void;
  placeholder?: string;
  minYear?: number;
}) {
  const today = new Date();
  const maxYear = today.getFullYear();

  const [open, setOpen] = useState(false);
  // The month currently shown in the grid (defaults to the value, else 2000-01).
  const initial = value ? value.split("-").map(Number) : [2000, 1];
  const [viewYear, setViewYear] = useState(initial[0]);
  const [viewMonth, setViewMonth] = useState(initial[1] - 1); // 0-based

  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click / Escape (subscribe to external events — allowed).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const shiftMonth = (delta: number) => {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setViewMonth(m);
    setViewYear(y);
  };

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const leadBlanks = (new Date(viewYear, viewMonth, 1).getDay() + 6) % 7; // Monday-first
  const maxTime = new Date(maxYear, today.getMonth(), today.getDate()).getTime();

  const isSelected = (d: number) => value === toISO(viewYear, viewMonth, d);
  const isFuture = (d: number) => new Date(viewYear, viewMonth, d).getTime() > maxTime;

  const years: number[] = [];
  for (let y = maxYear; y >= minYear; y--) years.push(y);

  return (
    <div className="rb-datepicker" ref={ref}>
      <button
        type="button"
        className={`rb-field-control rb-datepicker-trigger csx3-Rs1 ${value ? "" : "is-placeholder"}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span>{value ? formatDisplay(value) : placeholder}</span>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
          <rect x="3" y="4.5" width="18" height="16" rx="2" />
          <path d="M3 9h18M8 2.5v4M16 2.5v4" />
        </svg>
      </button>

      {open && (
        <div className="rb-datepicker-pop csx3-C00 csx3-g-12" role="dialog" aria-label="Choose date">
          <div className="rb-datepicker-head csx3-Rs1">
            <button type="button" className="rb-datepicker-nav" onClick={() => shiftMonth(-1)} aria-label="Previous month">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
            </button>
            <div className="csx3-R01 csx3-g-8">
              <select className="rb-datepicker-select" value={viewMonth} onChange={(e) => setViewMonth(Number(e.target.value))} aria-label="Month">
                {MONTHS.map((mo, i) => <option key={mo} value={i}>{mo}</option>)}
              </select>
              <select className="rb-datepicker-select" value={viewYear} onChange={(e) => setViewYear(Number(e.target.value))} aria-label="Year">
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button type="button" className="rb-datepicker-nav" onClick={() => shiftMonth(1)} aria-label="Next month">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
            </button>
          </div>

          <div className="rb-datepicker-grid">
            {WEEKDAYS.map((w) => <span key={w} className="rb-datepicker-wd">{w}</span>)}
            {Array.from({ length: leadBlanks }).map((_, i) => <span key={`b${i}`} />)}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const d = i + 1;
              const disabled = isFuture(d);
              return (
                <button
                  key={d}
                  type="button"
                  className={`rb-datepicker-day ${isSelected(d) ? "is-selected" : ""}`}
                  disabled={disabled}
                  onClick={() => { onChange(toISO(viewYear, viewMonth, d)); setOpen(false); }}
                >
                  {d}
                </button>
              );
            })}
          </div>

          <div className="rb-datepicker-foot csx3-Rs1">
            <button type="button" className="rb-link-btn" onClick={() => setOpen(false)}>
              {/* Source in IT: "Close" is ambiguous (→ "Vicino"); "Chiudi" translates correctly. */}
              <T id="common.close" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
